import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Truck, Wallet, History, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { fmtErr } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { UNIT_OPTIONS, unitLabel, inr, fmtDate, fmtDateTime, StatusBadge, badgeClass } from "@/lib/utils";

const FILTERS = ["All", "Open", "Order Placed", "Under Review", "Confirmed", "Out for Delivery", "Delivered", "Completed", "Cancelled"];

export default function AdminOrders() {
  const { t, tAny } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [modifyLine, setModifyLine] = useState(null);
const [shortLine, setShortLine] = useState(null);
const [shortReason, setShortReason] = useState("");
  const [modQty, setModQty] = useState("");
  const [modUnit, setModUnit] = useState("MT");
  const [payForm, setPayForm] = useState({ amount: "", method: "Cash", reference: "" });
  const [paymentTotalForm, setPaymentTotalForm] = useState("");
  const [deliveryForm, setDeliveryForm] = useState({});
  const [busy, setBusy] = useState(false);
const [customerPhoto, setCustomerPhoto] = useState(null);

const loadCustomerPhoto = async (path) => {
  if (!path) {
    setCustomerPhoto(null);
    return;
  }
  try {
    const response = await api.get(
      path.startsWith("/api/") ? path.slice(4) : path,
      { responseType: "blob" }
    );
    const url = URL.createObjectURL(response.data);
    setCustomerPhoto((old) => {
      if (old) URL.revokeObjectURL(old);
      return url;
    });
  } catch (e) {
    setCustomerPhoto(null);
  }
};

  const load = () => {
    api
      .get("/admin/orders", { params: { status: filter, q } })
      .then((r) => setOrders(r.data))
      .catch((e) => toast.error(fmtErr(e)));
  };

  useEffect(() => { load(); }, [filter]);

useEffect(() => {
  loadCustomerPhoto(selected?.customer?.photo_url);
  return () => {
    setCustomerPhoto((old) => {
      if (old) URL.revokeObjectURL(old);
      return null;
    });
  };
}, [selected?.customer?.photo_url]);

  const openOrder = async (id) => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setSelected(data);
      setDeliveryForm({
        delivery_date: data.delivery?.date || "",
        vehicle_number: data.delivery?.vehicle_number || "",
        driver_name: data.delivery?.driver_name || "",
        driver_phone: data.delivery?.driver_phone || "",
        status: data.delivery?.status || "Pending",
        notes: data.delivery?.notes || "",
      });
      setPayForm({ amount: "", method: "Cash", reference: "" });
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) openOrder(openId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refresh = async () => {
    load();
    if (selected) await openOrder(selected.id);
  };

  const lineAction = async (item, action, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/items/${item.id}`, { action, ...extra });
      setSelected({ ...selected, ...data });
      toast.success(`Line ${action === "cancel" ? "cancelled" : "updated"}`);
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (status) => {
    if (!window.confirm(`Set order status to "${status}"?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/status`, { status });
      setSelected({ ...selected, ...data });
      toast.success(`Status updated: ${status}`);
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const saveDelivery = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${selected.id}/delivery`, deliveryForm);
      setSelected({ ...selected, ...data });
      toast.success("Delivery information updated");
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const setPaymentTotal = async () => {
    if (!paymentTotalForm || Number(paymentTotalForm) <= 0) {
      toast.error("Enter a valid total amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/set-total", { order_id: selected.id, total: Number(paymentTotalForm) });
      setSelected({ ...selected, ...data });
      setPaymentTotalForm(String(data.payment?.total || ""));
      toast.success("Agreed total amount saved");
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (selected?.payment?.manual_total != null) {
      setPaymentTotalForm(String(selected.payment.manual_total));
    } else {
      setPaymentTotalForm("");
    }
  }, [selected?.id, selected?.payment?.manual_total]);

  const recordPayment = async () => {
    if (!payForm.amount || Number(payForm.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/payments/record", { order_id: selected.id, amount: Number(payForm.amount), method: payForm.method, reference: payForm.reference || null });
      setSelected({ ...selected, ...data });
      setPayForm({ amount: "", method: "Cash", reference: "" });
      toast.success("Payment recorded");
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const verifyPayment = async (paymentId) => {
    try {
      const { data } = await api.post(`/admin/payments/${paymentId}/verify`);
      setSelected({ ...selected, ...data });
      toast.success("Payment verified");
      await refresh();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  if (selected) {
    const editable = !["Cancelled", "Completed", "Delivered"].includes(selected.status);
    return (
      <div className="space-y-6" data-testid="admin-order-detail">
        <div>
          <button data-testid="back-to-orders-btn" className="sf-link text-sm inline-flex items-center gap-1" onClick={() => { setSelected(null); setSearchParams({}); }}>
            <ArrowLeft className="h-4 w-4" /> {t("allOrders")}
          </button>
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <h1 className="font-display text-2xl font-bold text-slate-900 font-mono-sf">{selected.order_number}</h1>
            <StatusBadge status={selected.status} />
            <StatusBadge status={selected.payment.status} testid={`admin-payment-badge-${selected.order_number}`} />
            <span className="text-sm text-slate-400">{fmtDateTime(selected.created_at)}</span>
          </div>
        </div>

        <div className="sf-card p-5 text-sm" data-testid="admin-customer-info">
          <div className="grid lg:grid-cols-[1fr_170px] gap-5">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="sf-label">{t("customerLbl")}</div>
                <div className="font-semibold text-slate-900 mt-1">{selected.customer?.name || "—"}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="sf-label">{t("mobileLbl")}</div>
                <div className="font-semibold text-slate-900 mt-1">+91 {selected.customer?.mobile || "—"}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="sf-label">{t("licenceNumber")}</div>
                <div className="font-semibold text-slate-900 mt-1">{selected.customer?.license_number || "—"}</div>
              </div>
              <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50 p-3">
                <div className="sf-label text-indigo-700">POS ID (Mandatory)</div>
                <div className="font-bold text-indigo-900 mt-1">{selected.customer?.pos_id || "—"}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="sf-label">{t("preferredDelivery")}</div>
                <div className="font-semibold text-slate-900 mt-1">{selected.preferred_delivery_date ? fmtDate(selected.preferred_delivery_date) : "—"}</div>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="sf-label">Customer ID</div>
                <div className="font-mono text-xs text-slate-700 mt-1 break-all">{selected.customer?.id || selected.customer_id || "—"}</div>
              </div>
              {selected.notes && (
                <div className="sm:col-span-2 xl:col-span-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="sf-label text-amber-700">{t("customerNote")}</div>
                  <div className="text-slate-700 mt-1">{selected.notes}</div>
                </div>
              )}
            </div>
            {customerPhoto && (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 flex flex-col items-center justify-center">
                <div className="sf-label mb-2">Customer Photo</div>
                <img src={customerPhoto} alt={selected.customer?.name || "Customer"} className="w-28 h-28 rounded-lg object-cover border border-slate-300 shadow-sm" />
                <button type="button" onClick={() => window.open(customerPhoto, "_blank")} className="mt-2 text-xs font-semibold text-blue-700 hover:text-blue-900">View Full Size</button>
              </div>
            )}
          </div>
        </div>

        <div className="sf-card overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200">
            <h2 className="font-display font-bold text-slate-900">{t("reviewTitle")}</h2>
          </div>
          <table className="w-full">
            <thead>
              <tr>
                <th className="sf-th">{t("company")}</th>
                <th className="sf-th">{t("product")}</th>
                <th className="sf-th">{t("requestedQty")}</th>
                <th className="sf-th">{t("approved")}</th>
                <th className="sf-th">{t("ratePerMtShort")}</th>
                <th className="sf-th text-right">{t("amount")}</th>
                <th className="sf-th">{t("lineStatus")}</th>
                {editable && <th className="sf-th text-right">{t("actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {selected.items.map((l) => (
                <tr key={l.id} data-testid={`admin-line-${l.id}`}>
                  <td className="sf-td">{l.company_name}</td>
                  <td className="sf-td font-semibold text-slate-900">{l.product_name}</td>
                  <td className="sf-td">
                    {l.requested_qty} {unitLabel(l.requested_unit)}
                  </td>
                  <td className="sf-td">{l.approved_qty ? `${l.approved_qty} ${unitLabel(l.approved_unit)}` : "—"}</td>
                  <td className="sf-td">{l.rate_per_mt != null ? inr(l.rate_per_mt) : "Call"}</td>
                  <td className="sf-td text-right font-semibold">{l.line_total != null ? inr(l.line_total) : "—"}</td>
                  <td className="sf-td">
                    <StatusBadge status={l.line_status} />
                  </td>
                  {editable && (
                    <td className="sf-td text-right">
                      <div className="inline-flex gap-1.5">
                        {l.line_status !== "Cancelled" && (
                          <>
                            <button data-testid={`approve-line-${l.id}`} className="text-xs font-bold px-2.5 py-1 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors" onClick={() => lineAction(l, "approve")} disabled={busy}>
                              {t("approve")}
                            </button>
                            <button                              data-testid={`modify-line-${l.id}`}
                              className="text-xs font-bold px-2.5 py-1 rounded-md border border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100 transition-colors"
                              onClick={() => {
                                setModifyLine(l);
                                setModQty(l.approved_qty || l.requested_qty);
                                setModUnit(l.approved_unit || l.requested_unit);
                              }}
                            >
                              {t("modify")}
                            </button>
                            <button className='text-xs font-bold px-2.5 py-1 rounded-md border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 transition-colors' onClick={() => { setShortLine(l); setShortReason(''); }} disabled={busy}>Product Short</button>
                            <button data-testid={`cancel-line-${l.id}`} className="text-xs font-bold px-2.5 py-1 rounded-md border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100 transition-colors" onClick={() => window.confirm(`Cancel ${l.product_name}?`) && lineAction(l, "cancel")} disabled={busy}>
                              {t("cancel")}
                            </button>
                          </>
                        )}
                        {l.line_status === "Cancelled" && <span className="text-xs text-slate-400">{l.admin_note || "Cancelled"}</span>}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap gap-2" data-testid="order-status-actions">
          {["Under Review", "Confirmed", "Payment Pending", "Payment Completed", "Preparing for Delivery", "Out for Delivery", "Delivered", "Completed", "Cancelled"].map((s) => (
            <button
              key={s}
              data-testid={`set-status-${s.toLowerCase().replace(/\s+/g, "-")}-btn`}
              disabled={busy || selected.status === s}
              onClick={() => setStatus(s)}
              className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-40 ${badgeClass(s)} hover:opacity-80`}
            >
              {s === selected.status ? `✓ ${tAny(s)}` : tAny(s)}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <div className="sf-card p-5" data-testid="admin-payment-section">
            <div className="flex items-center gap-2 font-display font-bold text-slate-900 mb-3">
              <Wallet className="h-4 w-4" /> {t("paymentsTitle")}
            </div>
            {selected.items.some((l) => l.rate_per_mt == null) && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 mb-4">
                <div className="sf-label text-amber-900">Call for Rate — Agreed Total Amount</div>
                <div className="flex gap-2 mt-2">
                  <input
                    data-testid="call-rate-total-input"
                    type="number"
                    min="0"
                    className="sf-input flex-1"
                    placeholder="Enter final agreed total amount"
                    value={paymentTotalForm}
                    onChange={(e) => setPaymentTotalForm(e.target.value)}
                  />
                  <button
                    data-testid="set-call-rate-total-btn"
                    className="sf-btn-primary whitespace-nowrap"
                    onClick={setPaymentTotal}
                    disabled={busy}
                  >
                    {selected.payment?.manual_total ? "Update Total" : "Set Total"}
                  </button>
                </div>
                <div className="text-xs text-amber-800 mt-2">
                  Enter the final amount agreed between the retailer/dealer and wholesale dealer. Paid amount and due will be calculated from this total.
                </div>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3 text-sm mb-4">
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="sf-label">{t("total")}</div>
                <div className="font-bold text-slate-900">{selected.payment.total > 0 ? inr(selected.payment.total) : t("ratePending")}</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="sf-label">{t("paid")}</div>
                <div className="font-bold text-emerald-700">{inr(selected.payment.paid)}</div>
              </div>
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="sf-label">{t("due")}</div>
                <div className="font-bold text-rose-700">{inr(selected.payment.due)}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <input data-testid="payment-amount-input" type="number" min="0" className="sf-input" placeholder={t("amountRupee")} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              <select data-testid="payment-method-select" className="sf-input" value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}>
                <option>Cash</option>
                <option>Online</option>
              </select>
              <input data-testid="payment-reference-input" className="sf-input" placeholder={t("refTxn")} value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} />
            </div>
            <button data-testid="record-payment-btn" className="sf-btn-primary w-full mt-2" onClick={recordPayment} disabled={busy}>
              {t("recordPayment")}
            </button>
            <div className="mt-3 space-y-1.5 text-xs">
              {(selected.payments || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2" data-testid={`admin-payment-record-${p.id}`}>
                  <span className="text-slate-600">
                    {fmtDateTime(p.created_at)} · <span className="font-semibold text-slate-900">{inr(p.amount)}</span> · {p.method}
                    {p.mode === "upi_qr" && " (customer-claimed UPI)"}
                    {p.reference ? ` · ${p.reference}` : ""}
                  </span>
                  {p.status === "pending" ? (
                    <button data-testid={`verify-payment-${p.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-800 hover:bg-amber-100" onClick={() => verifyPayment(p.id)}>
                      <ShieldCheck className="h-3 w-3" /> Verify
                    </button>
                  ) : (
                    <span className={`rounded-full border px-2 py-0.5 font-semibold ${badgeClass(p.status)}`}>{p.status}</span>
                  )}
                </div>
              ))}
              {(selected.payments || []).length === 0 && <div className="text-slate-400">{t("noPaymentsYet")}</div>}
            </div>
          </div>

          <div className="sf-card p-5" data-testid="admin-delivery-section">
            <div className="flex items-center gap-2 font-display font-bold text-slate-900 mb-3">
              <Truck className="h-4 w-4" /> {t("deliveryDispatchAdmin")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="sf-label">{t("deliveryDateField")}</label>
                <input data-testid="delivery-date-field" type="date" className="sf-input" value={deliveryForm.delivery_date || ""} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_date: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("vehicleNumber")}</label>
                <input data-testid="vehicle-number-input" className="sf-input font-mono-sf" placeholder="WB XX XXXX" value={deliveryForm.vehicle_number || ""} onChange={(e) => setDeliveryForm({ ...deliveryForm, vehicle_number: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("driverName")}</label>
                <input data-testid="driver-name-input" className="sf-input" value={deliveryForm.driver_name || ""} onChange={(e) => setDeliveryForm({ ...deliveryForm, driver_name: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("driverPhone")}</label>
                <input data-testid="driver-phone-input" className="sf-input" value={deliveryForm.driver_phone || ""} onChange={(e) => setDeliveryForm({ ...deliveryForm, driver_phone: e.target.value })} />
              </div>
              <div>
                <label className="sf-label">{t("dispatchStatus")}</label>
                <select data-testid="delivery-status-select" className="sf-input" value={deliveryForm.status || "Pending"} onChange={(e) => setDeliveryForm({ ...deliveryForm, status: e.target.value })}>
                  {["Pending", "Approved", "Ready for Dispatch", "Dispatched", "Delivered", "Cancelled"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="sf-label">{t("notes")}</label>
                <input data-testid="delivery-notes-input" className="sf-input" value={deliveryForm.notes || ""} onChange={(e) => setDeliveryForm({ ...deliveryForm, notes: e.target.value })} />
              </div>
            </div>
            <button data-testid="save-delivery-btn" className="sf-btn-primary w-full mt-3" onClick={saveDelivery} disabled={busy}>
              {t("saveDelivery")}
            </button>
          </div>
        </div>

        <div className="sf-card p-5" data-testid="admin-timeline">
          <div className="flex items-center gap-2 font-display font-bold text-slate-900 mb-3">
            <History className="h-4 w-4" /> {t("activityTimeline")}
          </div>
          <ol className="space-y-2">
            {(selected.timeline || []).slice().reverse().map((ev, i) => (
              <li key={i} className="flex gap-3 text-sm" data-testid={`admin-timeline-item-${i}`}>
                <span className="h-2 w-2 rounded-full bg-[#1E3E62] mt-1.5 shrink-0" />
                <div>
                  <span className="font-semibold text-slate-900">{tAny(ev.event)}</span>
                  {ev.detail && <span className="text-slate-600"> — {ev.detail}</span>}
                  <div className="text-xs text-slate-400">
                    {fmtDateTime(ev.at)} · {t("by")} {ev.by}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <Dialog open={!!shortLine} onOpenChange={(o) => !o && setShortLine(null)}>
          <DialogContent className="max-w-sm bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="product-short-dialog">
            <DialogHeader>
              <DialogTitle>Product Short / Not Available</DialogTitle>
              
                Record the shortage and tell the customer why the requested quantity cannot be supplied.
              
            </DialogHeader>

            {shortLine && (
              <div className="space-y-3">
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <div className="font-semibold text-slate-900">{shortLine.product_name}</div>
                  <div className="text-sm text-slate-600">
                    Requested: {shortLine.requested_qty} {shortLine.requested_unit}
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium text-slate-700">Reason</label>
                  <textarea
                    className="mt-1 w-full rounded-md border border-slate-300 p-2 text-sm"
                    rows={3}
                    value={shortReason}
                    onChange={(e) => setShortReason(e.target.value)}
                    placeholder="Example: Only 1 MT available in stock"
                  />
                </div>

                <div className="flex justify-end gap-2">
                  {selected?.customer?.mobile && <a href={`tel:+91${selected.customer.mobile}`} className="px-3 py-2 rounded-md border border-blue-300 bg-blue-50 text-blue-800 font-semibold">Contact Customer</a>}

                  <button
                    type="button"
                    className="px-3 py-2 rounded-md border"
                    onClick={() => setShortLine(null)}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="px-3 py-2 rounded-md bg-amber-600 text-white font-semibold"
                    onClick={async () => {
                      await lineAction(shortLine, "short", {
                        note: shortReason.trim() || "Product short / insufficient stock"
                      });
                      setShortLine(null);
                    }}
                  >
                    Confirm Product Short
                  </button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
        <Dialog open={!!modifyLine} onOpenChange={(o) => !o && setModifyLine(null)}>
          <DialogContent className="max-w-sm bg-white text-slate-900 border-slate-300 shadow-2xl" data-testid="modify-qty-dialog">
            <DialogHeader>
              <DialogTitle>{t("modifyTitle")}</DialogTitle>
            </DialogHeader>
            {modifyLine && (
              <div className="space-y-4">
                <div className="text-sm rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <div className="font-semibold text-slate-900">{modifyLine.product_name}</div>
                  <div className="text-slate-500">
                    {t("requestedQty")}: {modifyLine.requested_qty} {unitLabel(modifyLine.requested_unit)}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="sf-label">{t("approvedQty")}</label>
                    <input data-testid="approved-qty-input" type="number" min="0" step="any" className="sf-input" value={modQty} onChange={(e) => setModQty(e.target.value)} />
                  </div>
                  <div>
                    <label className="sf-label">{t("unit")}</label>
                    <select data-testid="approved-unit-select" className="sf-input" value={modUnit} onChange={(e) => setModUnit(e.target.value)}>
                      {UNIT_OPTIONS.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <DialogFooter>
                  <button className="sf-btn-outline" onClick={() => setModifyLine(null)}>
                    {t("cancel")}
                  </button>
                  <button
                    data-testid="save-approved-qty-btn"
                    className="sf-btn-primary"
                    disabled={busy}
                    onClick={async () => {
                      await lineAction(modifyLine, "modify", { approved_qty: Number(modQty), approved_unit: modUnit });
                      setModifyLine(null);
                    }}
                  >
                    {t("saveNotify")}
                  </button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("orders")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("adminOrdersSub")}</p>
        </div>
        <div className="relative">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="admin-order-search"
            className="sf-input pl-9 w-64"
            placeholder={t("searchOrders")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="order-filter-pills">
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
            onClick={() => setFilter(f)}
            className={`text-xs font-bold px-3.5 py-1.5 rounded-full border transition-colors ${
              filter === f ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
            }`}
          >
            {f === "All" ? t("filterAll") : f === "Open" ? t("filterOpen") : tAny(f)}
          </button>
        ))}
      </div>

      <div className="sf-card overflow-x-auto">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr>
              <th className="sf-th">{t("orderNo")}</th>
              <th className="sf-th">{t("customerLbl")}</th>
              <th className="sf-th">{t("mobileLbl")}</th>
              <th className="sf-th">{t("products")}</th>
              <th className="sf-th">{t("total")}</th>
              <th className="sf-th">{t("due")}</th>
              <th className="sf-th">{t("date")}</th>
              <th className="sf-th">{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((o) => (
              <tr key={o.id} className="sf-tr cursor-pointer" data-testid={`admin-order-row-${o.order_number}`} onClick={() => openOrder(o.id)}>
                <td className="sf-td font-mono-sf font-semibold text-slate-900">{o.order_number}</td>
                <td className="sf-td">{o.customer?.name}</td>
                <td className="sf-td">{o.customer?.mobile}</td>
                <td className="sf-td">{o.items.length}</td>
                <td className="sf-td">{o.payment.total > 0 ? inr(o.payment.total) : "—"}</td>
                <td className="sf-td font-semibold">{inr(o.payment.due)}</td>
                <td className="sf-td">{fmtDate(o.created_at)}</td>
                <td className="sf-td">
                  <StatusBadge status={o.status} />
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={8}>
                  No orders found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
